import React, { useEffect, useRef, useState } from 'react'
import { Color, Direction } from '../../../../utils/constants'
import Background from '../../../../assets/backgrounds/application-note.svg'
import PageHeader from '../../../../partials/PageHeader'
import api from '../../../../utils/api'
import Category, { CategoryType } from '../../../../components/Category'
import Button from '../../../../components/Button/Button'
import Tag from '../../../../components/Tag'
import SectionWithTag from '../../../../components/SectionWithTag'
import ApplicationNoteDetails from './ApplicationNoteDetails'
import ApplicationNoteForm from './ApplicationNoteForm'
import CK from 'js-cookie'
import { WP_REST_API_Post } from 'wp-types/index'
import { FormattedMessage } from 'react-intl'
import SimilarResources from '../SimilarResources'
import FadeIn from '../../../../components/Animation/FadeIn'
import { useIsMobile } from '../../../../utils/helpers'
import Modal from '../../../../components/Modal'
import { ReactComponent as Cross } from '../../../../assets/icons/cross.svg'
import DownloadNote from './DownloadNote'

interface ApplicationNoteProps {
  resource: WP_REST_API_Post | null
  category: CategoryType
  tags: string[]
}

const ApplicationNote = ({ resource, category, tags }: ApplicationNoteProps) => {
    const [formSubmitted, setFormSubmitted] = useState(false)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const isMobile = useIsMobile()
    const headerRef = useRef<HTMLDivElement>(null)
    const [padding, setPadding] = useState<{ paddingLeft: number; paddingRight: number } | null>(null)

    useEffect(() => {
        const email = CK.get('application_note')
        setFormSubmitted(!!email)
    }, [])

    const openFile = () => {
    // @ts-ignore
        api.get(`/wp-json/wp/v2/media/${resource.acf['application_note']}`).then((response) => {
            window.open(response.data['source_url'], '_blank')
        })
    }

    useEffect(() => {
        const calculatePadding = () => {
            requestAnimationFrame(() => {
                if (headerRef.current && !isMobile) {
                    setPadding({
                        paddingLeft: headerRef.current.getBoundingClientRect().left,
                        paddingRight: window.innerWidth - headerRef.current.getBoundingClientRect().right,
                    })
                } else {
                    setPadding(null)
                }
            })
        }

        calculatePadding()
        window.addEventListener('resize', calculatePadding)
        return () => {
            window.removeEventListener('resize', calculatePadding)
        }
    }, [headerRef.current, isMobile])

    return (
        <div className='bg-white'>
            <PageHeader
                bgColor={Color.SAND}
                component={Background}
                link={`/resources/${category}`}
                linkTitle={['page.resources', `page.resources.${category}`]}
            >
                <div
                    style={
                        padding
                            ? {
                                paddingLeft: `calc(${padding.paddingLeft}px - 3.5rem)`,
                                paddingRight: `calc(${padding.paddingRight}px - 3.5rem)`,
                            }
                            : undefined
                    }
                    className='flex md:items-end md:flex-row flex-col pt-12 pb-20 w-full side-padding md:gap-x-6 md:justify-between'
                >
                    <FadeIn appearFromBottom={!isMobile}>
                        <div className='max-w-[744px]'>
                            <Category type={category} className='bg-transparent backdrop-blur-none px-0' />
                            <h3 className='py-8'>{resource?.title?.rendered}</h3>
                            <Button
                                outerClasses='w-full md:w-auto ga-cta'
                                buttonColor={Color.PURPLE}
                                isInverted
                                chevron={Direction.RIGHT}
                                id={resource?.slug}
                                onClick={() => {
                                    if (formSubmitted) {
                                        openFile()
                                    } else {
                                        const form = document.getElementById('application-note-form') as HTMLDivElement
                                        form.scrollIntoView({ behavior: 'smooth' })
                                    }
                                }}
                            >
                                <FormattedMessage id={`page.resources.get.${category}`} tagName='h6' />
                            </Button>
                        </div>
                    </FadeIn>
                    {tags.length > 0 && (
                        <FadeIn>
                            <div className='max-w-[240px] pt-7 md:p-0'>
                                <h6 className='pb-2'>Tags:</h6>
                                <div className='flex flex-wrap gap-2'>
                                    {tags.map((tag) => (
                                        <Tag title={tag} color={Color.BLACK} key={tag} />
                                    ))}
                                </div>
                            </div>
                        </FadeIn>
                    )}
                </div>
            </PageHeader>
            <div className='py-12 md:py-30 mx-auto md:px-0 2xl:max-w-screen-2xl xl:max-w-screen-xl'>
                <SectionWithTag tag={{ id: `category.${category}` }}>
                    {resource && (
                        <div className='flex justify-between items-start flex-col lg:flex-row' ref={headerRef}>
                            <FadeIn>
                                <ApplicationNoteDetails
                                    post={resource}
                                    classes={{ outer: formSubmitted ? 'max-w-[744px]' : 'max-w-[520px]' }}
                                >
                                    <h5 className='lg:max-w-[470px] pt-8 lg:pt-0 pb-8'>
                                        <FormattedMessage id='page.resources.shareDetails' />
                                        <FormattedMessage id={`page.resources.get.${category}`} />
                                    </h5>
                                </ApplicationNoteDetails>
                            </FadeIn>
                            {formSubmitted || (
                                <FadeIn appearFromBottom>
                                    <div
                                        className='py-12 lg:p-14 border-y lg:border border-black/10'
                                        id='application-note-form'
                                    >
                                        <ApplicationNoteForm
                                            onSubmit={() => {
                                                setIsModalOpen(true)
                                                setFormSubmitted(true)
                                            }}
                                            category={category}
                                            id={resource.slug}
                                        />
                                    </div>
                                </FadeIn>
                            )}
                        </div>
                    )}
                </SectionWithTag>
            </div>
            <SimilarResources category={category} currentPostId={resource?.id} />
            {isModalOpen && (
                <Modal close={() => setIsModalOpen(false)}>
                    <div className='bg-white pb-16 side-padding md:px-28 pt-[74px] flex flex-col items-center'>
                        <Button
                            outerClasses='absolute right-0 top-0 m-4 border-black/20'
                            innerClasses='p-4'
                            onClick={() => setIsModalOpen(false)}
                        >
                            <Cross className='w-3.5 h-3.5' />
                        </Button>
                        <DownloadNote
                            onClick={() => {
                                formSubmitted && openFile()
                                setIsModalOpen(false)
                            }}
                            className='max-w-[500px]'
                        />
                    </div>
                </Modal>
            )}
        </div>
    )
}

export default ApplicationNote
